import React, { createContext, useState, useEffect } from 'react';
import { AuthType } from '../../../generated/graphql';
import { getTokenExpiration } from '../utils/token';
import { AuthContextType } from '../models/types';
import { client } from '@/apollo';

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthType['user'] | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tokenExpiration, setTokenExpiration] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing auth data in localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const isCypress =
      typeof window !== 'undefined' &&
      !!(window as { Cypress?: unknown }).Cypress;

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (isCypress) {
          setToken(storedToken);
          setUser(parsedUser);
          // Keep test sessions stable without depending on JWT parsing details.
          setTokenExpiration(new Date(Date.now() + 24 * 60 * 60 * 1000));
        } else {
          const expiration = getTokenExpiration(storedToken);
          if (expiration && expiration > new Date()) {
            // Only restore if token hasn't expired
            setToken(storedToken);
            setUser(parsedUser);
            setTokenExpiration(expiration);
          } else {
            // Clear expired data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        }
      } catch (error) {
        console.error('Failed to restore authentication state:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  // Set up automatic logout when token expires
  useEffect(() => {
    if (tokenExpiration) {
      const timeUntilExpiry = tokenExpiration.getTime() - new Date().getTime();
      if (timeUntilExpiry > 0) {
        const logoutTimer = setTimeout(logout, timeUntilExpiry);
        return () => clearTimeout(logoutTimer);
      } else {
        // Token already expired
        logout();
      }
    }
  }, [tokenExpiration]);

  const login = async (auth: AuthType) => {
    const expiration = getTokenExpiration(auth.token);
    if (!expiration) {
      console.error('Invalid token: no expiration found');
      return;
    }

    // IMPORTANT: Save token to localStorage FIRST before clearing cache
    // This ensures the authLink can read the token when queries refetch
    localStorage.setItem('token', auth.token);
    localStorage.setItem('user', JSON.stringify(auth.user));

    // Update state
    setUser(auth.user);
    setToken(auth.token);
    setTokenExpiration(expiration);

    // Clear Apollo Client cache AFTER token is saved
    // This will trigger refetches that will use the new token from localStorage
    await client.clearStore();
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setTokenExpiration(null);

    // Clear Apollo Client cache
    client.clearStore();

    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const isAuthenticated =
    !!token && !!user && !!tokenExpiration && tokenExpiration > new Date();

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !(window as { Cypress?: unknown }).Cypress
    ) {
      return;
    }

    (
      window as typeof window & {
        __AUTH_LOGIN__: typeof login;
      }
    ).__AUTH_LOGIN__ = login;

    return () => {
      delete (
        window as typeof window & {
          __AUTH_LOGIN__?: typeof login;
        }
      ).__AUTH_LOGIN__;
    };
  }, [login]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        token,
        tokenExpiration,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
